import { useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import useDeviceTelemetries from "./useDeviceTelemetries";
import { DjiCloudDrcState, DjiFlightJoystickAuthorityMode, DjiFlightJoystickAuthoritySwitchEvent } from "./deviceConstants";
import { setDeviceFlightAuthorityStatus } from "../DeviceServiceSlice";
import useDeviceInfo from "./useDeviceInfo";
import { getDeviceAuthorityInfo, getDeviceConnectionGroups, getDeviceInfo } from "./deviceUtils";
import useCloudLiveFlight from "src/services/mission/common/useCloudLiveFlight";
import { onboardConnectionManager } from "src/helper/HubConnectionManager";

export default function useDeviceFlightAuthority (deviceId) {
  const dispatch = useDispatch();
  const store = useStore();
  const liveFlight = useCloudLiveFlight();
  const [authorityMode, setAuthorityMode] = useState(DjiFlightJoystickAuthorityMode.RC);
  const [authorityModeReason, setAuthorityModeReason] = useState();
  const [activeRequest, setActiveRequest] = useState();
  const drcState = useDeviceTelemetries(deviceId, state => state?.drc_state);
  const deviceInfo = useDeviceInfo(deviceId);
  const authorityStatus = useSelector(state => state.deviceService.deviceFlightAuthorityStatus?.[deviceId]);

  const requestPsdkAuthority = (onboardDeviceId) => {
    const parentDeviceId = getDeviceInfo(onboardDeviceId)?.attachedToDock;
    const authorityInfo = getDeviceAuthorityInfo(parentDeviceId);
    const groups = getDeviceConnectionGroups(onboardDeviceId);

    if(authorityInfo?.authorityMode === DjiFlightJoystickAuthorityMode.DOCK) {
      liveFlight?.sendStopDrcChannel(parentDeviceId, {}, (data) => {
        console.log('🕹️ Received stop drc channel response for PSDK authority preparation:', {data});

        setTimeout(() => {
          onboardConnectionManager?.sendToGroup(onboardDeviceId, groups?.send?.commandChannel, {
            flightControl: {
              actionId: 1,
              altitude: null
            }
          });
        }, 8000);
      });
    } else {
      onboardConnectionManager?.sendToGroup(onboardDeviceId, groups?.send?.commandChannel, {
        flightControl: {
          actionId: 1,
          altitude: null
        }
      });
    }
  }

  useEffect(() => {
    if(drcState === DjiCloudDrcState.REQUESTED) {
      setActiveRequest(DjiFlightJoystickAuthorityMode.DOCK);
    }
    
    if(drcState === DjiCloudDrcState.ENABLED) {
      setAuthorityMode(DjiFlightJoystickAuthorityMode.DOCK);
      setAuthorityModeReason(DjiFlightJoystickAuthoritySwitchEvent.DOCK_DRC);
    }
  }, [drcState, deviceId]);

  useEffect(() => {
    if(!authorityStatus) {
      if(drcState === DjiCloudDrcState.DISABLED) {
        setAuthorityMode(DjiFlightJoystickAuthorityMode.RC);
        setAuthorityModeReason(DjiFlightJoystickAuthoritySwitchEvent.DOCK_DRC);
      }

      return;
    }

    setAuthorityMode(authorityStatus?.authorityMode);
    setAuthorityModeReason(authorityStatus?.authorityModeReason);
  }, [drcState, authorityStatus, deviceId]);

  useEffect(() => {
    if(authorityMode === authorityStatus?.requiredAuthorityError) {
      dispatch(setDeviceFlightAuthorityStatus({
        deviceId: deviceId,
        data: {
          requiredAuthorityError: null,
        }
      }));
    }
  }, [authorityMode, authorityStatus?.requiredAuthorityError, deviceId]); 
  
  useEffect(() => {
    const onboardDeviceId = deviceInfo?.onboardDevice;
    if(!onboardDeviceId) return;

    const onboardAuthorityStatus = store.getState().deviceService.deviceFlightAuthorityStatus?.[onboardDeviceId];
    if(onboardAuthorityStatus?.authorityMode === undefined) return;
    
    dispatch(setDeviceFlightAuthorityStatus({
      deviceId: deviceId,
      data: onboardAuthorityStatus
    }));

    console.log('🕹️ Synced dock flight authority info with paired onboard drone.', {deviceId, onboardDeviceId, onboardAuthorityStatus, authorityStatus});
  }, [deviceInfo?.onboardDevice]);

  return {
    authorityMode,
    authorityModeReason,
    activeRequest,
    requiredAuthorityError: authorityStatus?.requiredAuthorityError,
    requestPsdkAuthority,
  };
}